import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchItems, refetchSingleItem } from '../../firebase/course';
import { useMetadata } from '../../contexts/MetadataProvider';
import { useAuth } from '../../firebase/useAuth';
import { useModal } from '../../context/ModalContext';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import DetailsModal from './DetailsModal/DetailsModal';
import SearchInput from '../search/SearchInput';
import SearchResultCard from '../search/SearchResultCard';
import SearchResultCardSkeleton from '../search/skeletons/SearchResultCardSkeleton';
import ActionButton from '../common/ActionButton';
import { MagnifyingGlassIcon, ArrowRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

const MODAL_SEARCH_LIMIT = 5;

function SearchModal({ handleClose, style, onMouseDown }) {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('words');
  const [wordSearchBy, setWordSearchBy] = useState('kana');
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showModal, hideModal } = useModal();
  const navigate = useNavigate();
  const debounceTimeout = useRef(null);
  const { userId } = useAuth();
  const { learnedWordIds, learnedSentenceIds } = useMetadata();
  const learnedWordIdsRef = useRef(learnedWordIds);
  const learnedSentenceIdsRef = useRef(learnedSentenceIds);
  useEffect(() => { learnedWordIdsRef.current = learnedWordIds; }, [learnedWordIds]);
  useEffect(() => { learnedSentenceIdsRef.current = learnedSentenceIds; }, [learnedSentenceIds]);


  const fetchResults = useCallback(async () => {
    if (!query.trim()) {
      setResults([]);
      setHasMore(false);
      return;
    }
    setLoading(true);
    try {
      const { results: newResults, hasMore: newHasMore } = await searchItems(query, searchType, wordSearchBy, MODAL_SEARCH_LIMIT, userId, searchType === 'words' ? learnedWordIdsRef.current : learnedSentenceIdsRef.current);
      setResults(newResults);
      setHasMore(newHasMore);
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setLoading(false);
    }
  }, [query, searchType, wordSearchBy, userId]);

  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchResults();
    }, 300);
    return () => clearTimeout(debounceTimeout.current);
  }, [query, searchType, wordSearchBy, fetchResults]);

  // Clear results immediately when search type changes
  useEffect(() => {
    setResults([]);
    setHasMore(false);
  }, [searchType]);

  const handleModalSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    fetchResults();
  };

  // Handle item updates from DetailsModal actions
  const handleItemUpdate = useCallback(async (itemId) => {
    try {
      // Refetch the updated item data from server
      const updatedItem = await refetchSingleItem(itemId, userId, searchType);
      
      setResults(prevResults => {
        const itemIndex = prevResults.findIndex(item => item.id === itemId);
        if (itemIndex === -1) return prevResults;
        
        const newResults = [...prevResults];
        // Replace with the updated item data
        newResults[itemIndex] = updatedItem;
        return newResults;
      });
    } catch (error) {
      console.error('Failed to refetch item:', error);
    }
  }, [userId, searchType]);

  const handleResultClick = (item) => {
    showModal(<DetailsModal item={item} handleClose={hideModal} onUpdate={handleItemUpdate} />);
  };

  const handleShowAll = () => {
    navigate('/search', { state: { initialQuery: query, initialType: searchType, initialSearchBy: wordSearchBy } });
    handleClose();
  };

  // Logic for the footer result text pluralization
  const resultCountText = `${results.length} result${results.length !== 1 ? 's' : ''} found.`;

  return (
    <SkeletonTheme baseColor="var(--color-skeleton-base)" highlightColor="var(--color-skeleton-highlight)">
      <div
        className="w-full max-w-2xl bg-background-secondary rounded-2xl shadow-2xl flex flex-col border border-border border-t-4 overflow-hidden"
        style={{
          borderTopColor: 'var(--color-primary)',
          ...style,
        }}
        onMouseDown={onMouseDown}
      >
        <SearchInput
          searchQuery={query}
          setSearchQuery={setQuery}
          searchType={searchType}
          setSearchType={setSearchType}
          wordSearchBy={wordSearchBy}
          setWordSearchBy={setWordSearchBy}
          onSubmit={handleModalSearchSubmit}
          onClose={handleClose}
          variant="modal"
          wrapperClassName="flex-shrink-0"
        />

        <div className="relative p-4 bg-background-primary max-h-[60vh] overflow-y-auto">
          {/* Layer 1: The list that dictates the height. */}
          <div className="flex flex-col gap-3">
            {loading ? (
              Array.from({ length: MODAL_SEARCH_LIMIT }).map((_, index) => <SearchResultCardSkeleton key={index} />)
            ) : results.length > 0 ? (
              <>
                {results.map((item) => (
                  <SearchResultCard key={item.id} item={item} type={searchType} onClick={() => handleResultClick(item)} learnedIds={searchType === 'words' ? learnedWordIds : learnedSentenceIds} />
                ))}
                {/* Invisible placeholders to fill space and maintain height */}
                {Array.from({ length: MODAL_SEARCH_LIMIT - results.length }).map((_, index) => (
                  <div key={`placeholder-${index}`} aria-hidden="true" className="invisible">
                    <SearchResultCardSkeleton />
                  </div>
                ))}
              </>
            ) : (
              // When empty, render invisible skeletons to preserve the exact height.
              Array.from({ length: MODAL_SEARCH_LIMIT }).map((_, index) => (
                <div key={index} aria-hidden="true" className="invisible">
                  <SearchResultCardSkeleton />
                </div>
              ))
            )}
          </div>

          {/* Layer 2: The text overlay with glassmorphic styling */}
          {!loading && results.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              {query ? (
                <div
                  className="
          relative flex flex-col items-center justify-center text-center
          p-8 rounded-2xl backdrop-blur-sm border border-border overflow-hidden
          max-w-sm w-full
        "
                  style={{
                    background: 'linear-gradient(135deg, var(--color-stat-streak)08, var(--color-stat-streak)15)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {/* Glassmorphic overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

                  {/* Content */}
                  <div className="relative z-10 flex flex-col items-center">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm border border-white/20"
                      style={{
                        background: 'var(--color-stat-streak)',
                        boxShadow: '0 4px 12px var(--color-stat-streak)30',
                      }}
                    >
                      <ExclamationTriangleIcon className="w-8 h-8 text-white drop-shadow-sm" />
                    </div>
                    <p className="text-content-primary font-semibold mb-2">No results found</p>
                    <p className="text-sm text-content-secondary">Try a different search term or check your spelling</p>
                  </div>
                </div>
              ) : (
                <div
                  className="
          relative flex flex-col items-center justify-center text-center
          p-8 rounded-2xl backdrop-blur-sm border border-border overflow-hidden
          max-w-sm w-full
        "
                  style={{
                    background: 'linear-gradient(135deg, var(--color-primary)08, var(--color-secondary)15)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {/* Glassmorphic overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

                  {/* Content */}
                  <div className="relative z-10 flex flex-col items-center">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm border border-white/20"
                      style={{
                        background: 'var(--color-primary)',
                        boxShadow: '0 4px 12px var(--color-primary)30',
                      }}
                    >
                      <MagnifyingGlassIcon className="w-8 h-8 text-white drop-shadow-sm" />
                    </div>
                    <p className="text-content-primary font-semibold mb-2">Start your search</p>
                    <p className="text-sm text-content-secondary">Type to search for words or sentences</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer: Dynamic footer with glassmorphic styling */}
        <div
          className="p-4 border-t border-border text-center min-h-[77px] flex items-center justify-center"
          style={{
            background: 'var(--color-background-secondary)',
          }}
        >
          {!loading && hasMore ? (
            <ActionButton
              variant="glassy"
              onClick={handleShowAll}
              icon={ArrowRightIcon}
              color="orange"
              className="w-full py-3 px-6 text-sm"
            >
              Show all results for &quot;{query}&quot;
            </ActionButton>
          ) : !loading && results.length > 0 ? (
            <div
              className="
                relative px-4 py-2 rounded-xl backdrop-blur-sm border border-border overflow-hidden
              "
              style={{
                background: 'linear-gradient(135deg, var(--color-accent)08, var(--color-accent)15)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              {/* Glassmorphic overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-30 dark:from-white/5 dark:opacity-20" />

              {/* Content */}
              <p className="relative z-10 text-sm text-content-secondary font-medium">{resultCountText}</p>
            </div>
          ) : (
            <span>&nbsp;</span>
          )}
        </div>
      </div>
    </SkeletonTheme>
  );
}

export default SearchModal;